import { useTranslation } from "react-i18next"
import { Badge, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui"
import type { MyBrandDrawerModel } from "../../lib/my-brand-drawer-model"
import type { MyBrandDrawerNarrativeContent } from "../../lib/my-brand-drawer-narrative"
import {
  DRAWER_CONCEPT_PILL_COLORS,
  DRAWER_SEQUENCE_PILL_COLORS,
  drawerPillColorAt,
} from "../../lib/drawer-pill-colors"
import { parseSequenceStartSeconds } from "../../lib/drawer-sequence-time"
import { avatarColor } from "@/lib/avatar-color"
import { DrilldownMyBrandCreativeMetricsGrid } from "./DrilldownMyBrandCreativeMetricsGrid"

interface DrilldownMyBrandCreativeInfoTabProps {
  model: MyBrandDrawerModel
  narrative: MyBrandDrawerNarrativeContent
  isVideo: boolean
  isLoading?: boolean
  isError?: boolean
  onSequenceSeek?: (seconds: number) => void
}

function NarrativeTextRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-start gap-x-4 gap-y-0">
      <p className="text-[10px] font-normal uppercase tracking-[0.06em] text-muted-foreground">{label}</p>
      <p className="text-[13px] leading-snug text-foreground">{value}</p>
    </div>
  )
}

const TEXT_NARRATIVE_FIELDS = [
  { key: "topics", labelKey: "drilldown.drawer.topics", defaultLabel: "Topics" },
  { key: "presenterAge", labelKey: "drilldown.drawer.presenterAge", defaultLabel: "Presenter Age" },
  { key: "targetAudience", labelKey: "drilldown.drawer.targetAudience", defaultLabel: "Target Audience" },
  { key: "angle", labelKey: "drilldown.drawer.angle", defaultLabel: "Angle" },
  { key: "painPoint", labelKey: "drilldown.drawer.painPoint", defaultLabel: "Pain Point" },
  { key: "motivation", labelKey: "drilldown.drawer.motivation", defaultLabel: "Motivation" },
  { key: "persona", labelKey: "drilldown.drawer.persona", defaultLabel: "Persona" },
] as const satisfies ReadonlyArray<{
  key: keyof Pick<
    MyBrandDrawerNarrativeContent,
    "topics" | "presenterAge" | "targetAudience" | "angle" | "painPoint" | "motivation" | "persona"
  >
  labelKey: string
  defaultLabel: string
}>

export function DrilldownMyBrandCreativeInfoTab({
  model,
  narrative,
  isVideo,
  isLoading = false,
  isError = false,
  onSequenceSeek,
}: DrilldownMyBrandCreativeInfoTabProps) {
  const { t } = useTranslation()
  const populatedTextFields = TEXT_NARRATIVE_FIELDS.flatMap((field) => {
    const value = narrative[field.key]
    return value === null ? [] : [{ ...field, value }]
  })

  return (
    <div className="flex flex-col gap-5 px-6 py-5" aria-busy={isLoading}>
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            {model.logoUrl ? (
              <img
                src={model.logoUrl}
                alt={model.brandName}
                className="size-8 shrink-0 rounded-full object-cover"
              />
            ) : (
              <span
                className="flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold uppercase text-white"
                style={{ backgroundColor: avatarColor(model.brandName) }}
              >
                {model.brandName.charAt(0) || "?"}
              </span>
            )}
          </TooltipTrigger>
          <TooltipContent side="top">{model.brandName}</TooltipContent>
        </Tooltip>
        <p className="text-sm font-medium text-foreground">{model.brandName}</p>
      </div>

      {isError ? (
        <p className="text-sm text-destructive">
          {t("drilldown.myBrandDrawer.loadError", "Failed to load creative details.")}
        </p>
      ) : null}

      <DrilldownMyBrandCreativeMetricsGrid model={model} isLoading={isLoading} />

      {isLoading ? (
        <p className="sr-only">{t("drilldown.myBrandDrawer.loading", "Loading creative…")}</p>
      ) : (
        <>
          {populatedTextFields.length > 0 ? (
            <div className="flex flex-col gap-3">
              {populatedTextFields.map((field) => (
                <NarrativeTextRow
                  key={field.key}
                  label={t(field.labelKey, field.defaultLabel)}
                  value={field.value}
                />
              ))}
            </div>
          ) : null}

          {narrative.concepts.length > 0 ? (
            <div className="flex flex-col">
              <p className="text-[10px] font-normal uppercase tracking-[0.06em] text-muted-foreground">
                {t("drilldown.drawer.concepts", "Concepts")}
              </p>
              <div className="flex flex-wrap gap-1.5 pt-2">
                {narrative.concepts.map((concept, index) => {
                  const colors = drawerPillColorAt(DRAWER_CONCEPT_PILL_COLORS, index)
                  return (
                    <span
                      key={`${concept.label}-${index}`}
                      className="inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs leading-[18px]"
                      style={{ backgroundColor: colors.backgroundColor, color: colors.color }}
                    >
                      {concept.label}
                      {concept.isPrimary ? (
                        <Badge variant="slate">
                          {t("drilldown.myBrandDrawer.primaryConceptBadge", "Primary")}
                        </Badge>
                      ) : null}
                    </span>
                  )
                })}
              </div>
            </div>
          ) : null}

          {isVideo && narrative.sequence.length > 0 ? (
            <div className="flex flex-col">
              <p className="text-[10px] font-normal uppercase tracking-[0.06em] text-muted-foreground">
                {t("drilldown.drawer.sequence", "Sequence")}
              </p>
              <div className="flex flex-wrap gap-1.5 pt-2">
                {narrative.sequence.map((item, index) => {
                  const colors = drawerPillColorAt(DRAWER_SEQUENCE_PILL_COLORS, index)
                  const startSeconds = item.timeRange ? parseSequenceStartSeconds(item.timeRange) : null
                  const canSeek = startSeconds !== null && onSequenceSeek

                  const pillClassName = canSeek
                    ? "inline-flex cursor-pointer rounded-lg border-0 px-2 py-0.5 text-xs leading-[18px] transition-opacity hover:opacity-80"
                    : "inline-flex rounded-lg px-2 py-0.5 text-xs leading-[18px]"
                  const pillStyle = { backgroundColor: colors.backgroundColor, color: colors.color }

                  const pill = canSeek ? (
                    <button
                      type="button"
                      className={pillClassName}
                      style={pillStyle}
                      onClick={() => onSequenceSeek(startSeconds)}
                    >
                      {item.label}
                    </button>
                  ) : (
                    <span className={pillClassName} style={pillStyle}>
                      {item.label}
                    </span>
                  )

                  if (!item.timeRange) {
                    return <span key={`${item.label}-${index}`}>{pill}</span>
                  }

                  return (
                    <Tooltip key={`${item.label}-${index}`}>
                      <TooltipTrigger asChild>{pill}</TooltipTrigger>
                      <TooltipContent side="top">{item.timeRange}</TooltipContent>
                    </Tooltip>
                  )
                })}
              </div>
            </div>
          ) : null}

          {isVideo && narrative.verbalHooks !== null ? (
            <div className="flex flex-col">
              <p className="text-[10px] font-normal uppercase tracking-[0.06em] text-muted-foreground">
                {t("drilldown.drawer.verbalHooks", "Verbal Hooks")}
              </p>
              <p className="pt-2 text-[13px] leading-[21px] text-foreground/90">{narrative.verbalHooks}</p>
            </div>
          ) : null}

          {narrative.textOverlay !== null ? (
            <div className="flex flex-col">
              <p className="text-[10px] font-normal uppercase tracking-[0.06em] text-muted-foreground">
                {t("drilldown.drawer.textOverlay", "Text Overlay")}
              </p>
              <p className="pt-2 text-[13px] leading-[21px] text-foreground/90">{narrative.textOverlay}</p>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
